import { useState } from 'react';
import {
    Search,
    Download,
    Calendar,
} from 'lucide-react';
import { useAdminStore } from '../../stores/adminStore';
import { Certificate } from '../../types';
import api from '../../services/api';

export function AdminCertificatesPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const { certificates, isLoading, error } = useAdminStore();

    const filteredCertificates = certificates.filter(cert => {
        const matchesSearch = (cert.issuedTo?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                            (cert.title?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                            (cert.employeeId?.toLowerCase() || '').includes(searchQuery.toLowerCase());
        return matchesSearch;
    });

    const handleDownloadAll = async () => {
        try {
            // Download each certificate individually using the API
            for (let i = 0; i < filteredCertificates.length; i++) {
                const certificate = filteredCertificates[i];
                
                try {
                    const response = await api.get(`/admin/certificates/${certificate.employeeId}/download`, {
                        params: {
                            courseTitle: certificate.title,
                            curriculumId: certificate.curriculum_id
                        },
                        responseType: 'blob'
                    });
                    
                    // Extract filename from Content-Disposition header
                    let filename = `certificate_${certificate.employeeId}.pdf`;
                    const contentDisposition = response.headers['content-disposition'];
                    if (contentDisposition) {
                        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
                        if (filenameMatch) {
                            filename = filenameMatch[1];
                        }
                    }
                    
                    // Create download link
                    const url = window.URL.createObjectURL(response.data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    
                    // Add a small delay between downloads to avoid overwhelming the browser
                    if (i < filteredCertificates.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 500));
                    }
                } catch (error) {
                    console.error(`Error downloading certificate ${certificate.id}:`, error);
                    // Continue with other certificates even if one fails
                }
            }
        } catch (error) {
            console.error('Error downloading certificates:', error);
            alert('Failed to download some certificates. Please try again.');
        }
    };

    const getExpiryStatus = (expiryDate: string) => {
        const today = new Date();
        const expiry = new Date(expiryDate);
        const monthsLeft = (expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 30);

        if (monthsLeft < 0) {
            return { text: 'Expired', class: 'bg-red-100 text-red-800' };
        } else if (monthsLeft < 3) {
            return { text: 'Expiring Soon', class: 'bg-amber-100 text-amber-800' };
        } else {
            return { text: 'Active', class: 'bg-green-100 text-green-800' };
        }
    };

    const handleDownloadCertificate = async (certificate: Certificate) => {
        try {
            
            // Call the admin API endpoint
            const response = await api.get(`/admin/certificates/${certificate.employeeId}/download`, {
                params: {
                    courseTitle: certificate.title,
                    curriculumId: certificate.curriculum_id
                },
                responseType: 'blob'
            });
            
            // Extract filename from Content-Disposition header
            let filename = `certificate_${certificate.employeeId}.pdf`;
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            
            // Create download link
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading certificate:', error);
            alert('Failed to download certificate. Please try again later.');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                <div className="flex flex-row items-center gap-2 w-full sm:w-auto">
                    <div className="relative w-full sm:w-80">
                        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search by name, ID or certificate..."
                            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={handleDownloadAll}
                        className="flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        Download All
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-600">{error}</p>
                    <p className="text-sm text-red-500 mt-2">Using mock data as fallback</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredCertificates.map((certificate, index) => {
                        const expiryStatus = getExpiryStatus(certificate.expiryDate || '');
                        // Create a unique key that handles duplicates and null IDs
                        const uniqueKey = certificate.id ? `${certificate.id}-${index}` : `cert-${index}`;
                        return (
                            <div key={uniqueKey} className="bg-white rounded-lg shadow-lg overflow-hidden">
                                <div className="relative h-48">
                                    <img
                                        src={certificate.image}
                                        alt={certificate.title}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute top-4 right-4">
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${expiryStatus.class}`}>
                                            {expiryStatus.text}
                                        </span>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <h3 className="text-lg font-semibold text-foreground mb-1">{certificate.title}</h3>
                                            <div className="flex items-center mb-4">
                                                <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center mr-2">
                                                    <span className="text-xs font-medium text-primary-600">
                                                        {certificate.issuedTo ? certificate.issuedTo.split(' ').map(n => n[0]).join('').toUpperCase() : '?'}
                                                    </span>
                                                </div>
                                                <span className="text-sm text-slate-600">{certificate.issuedTo || 'Unknown Employee'}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-center text-sm text-slate-500">
                                            <Calendar className="w-4 h-4 mr-2" />
                                            <span>Completed: {certificate.completedDate ? new Date(certificate.completedDate).toLocaleDateString() : certificate.issuedDate ? new Date(certificate.issuedDate).toLocaleDateString() : 'N/A'}</span>
                                        </div>
                                        <div className="flex items-center text-sm text-slate-500">
                                            <Calendar className="w-4 h-4 mr-2" />
                                            <span>Expires: {certificate.expiryDate ? new Date(certificate.expiryDate).toLocaleDateString() : 'N/A'}</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <button 
                                                onClick={() => handleDownloadCertificate(certificate)}
                                                className="flex items-center px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                            >
                                                <Download className="w-4 h-4 mr-2" />
                                                Download
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
} 